import { Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { PublicServicesService } from '../public-services/public-services.service';
import { IServiceTranslation } from '../../models/service-translation.model';

@Injectable({
  providedIn: 'root',
})
export class ServiceTranslationMergerService {
  constructor(readonly publicServicesService: PublicServicesService) {}
  /**
   * Loads service translations for a single service and merges them with existing platform translations.
   * Platform translations take precedence over service translations.
   *
   * @param serviceId - The ID of the service to load translations for
   * @param locale - The locale to load translations for
   * @param translateService - The TranslateService instance for managing translations
   * @param servicesBaseUrl - The base URL for the services API (optional, defaults to empty string)
   * @returns Observable that emits the service translations after merging
   */
  loadAndMergeServiceTranslation(
    serviceId: string,
    locale: string,
    translateService: TranslateService,
    servicesBaseUrl?: string
  ): Observable<IServiceTranslation> {
    const baseUrl = servicesBaseUrl || '';

    return this.publicServicesService
      .getServiceTranslationsByLocale(serviceId, locale, baseUrl)
      .pipe(
        map(response => {
          this.mergeTranslationsIntoPlatform(
            response.data,
            locale,
            translateService
          );
          return response.data;
        })
      );
  }

  /**
   * Loads service translations for multiple services and merges them with existing platform translations.
   * Platform translations take precedence over service translations.
   *
   * @param serviceIds - Array of service IDs to load translations for
   * @param locale - The locale to load translations for
   * @param translateService - The TranslateService instance for managing translations
   * @param servicesBaseUrl - The base URL for the services API (optional, defaults to empty string)
   * @returns Observable that emits the service translations after merging
   */
  loadAndMergeServicesTranslations(
    serviceIds: string[],
    locale: string,
    translateService: TranslateService
  ): Observable<IServiceTranslation> {
    return this.publicServicesService
      .getServicesTranslations(serviceIds, locale)
      .pipe(
        map(response => {
          this.mergeTranslationsIntoPlatform(
            response.data,
            locale,
            translateService
          );
          return response.data;
        })
      );
  }

  /**
   * Merges service translations with existing platform translations.
   * Platform translations take precedence over service translations.
   *
   * @param serviceTranslations - The service translations to merge
   * @param locale - The locale of the translations
   * @param translateService - The TranslateService instance for managing translations
   */
  private mergeTranslationsIntoPlatform(
    serviceTranslations: IServiceTranslation,
    locale: string,
    translateService: TranslateService
  ): void {
    if (!serviceTranslations || !translateService) {
      return;
    }

    // Get existing platform translations for the locale
    const existingPlatformTranslations =
      translateService.translations[locale] || {};

    // Get service translations for the locale
    const serviceTranslationsForLocale = serviceTranslations[locale] || {};

    // Merge translations with platform translations taking precedence
    const mergedTranslations = {
      ...serviceTranslationsForLocale,
      ...existingPlatformTranslations, // Existing platform translations take precedence
    };

    // Set the merged translations
    translateService.setTranslation(locale, mergedTranslations);
  }
}
